import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Radio,
  DatePicker,
  Upload,
  Button,
  ConfigProvider,
  message,
} from 'antd';
import {
  ImagePlus,
  Banknote,
  X as CloseIcon,
  HelpCircle,
} from 'lucide-react';
import type { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import { GoalType } from './useDonateCampaign';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (values: {
    title: string;
    content: string;
    imageUrl?: string;
    goalType: GoalType;
    targetAmount: number;
    startAt: string;
    endAt?: string | null;
  }) => void;
}

const BRAND = '#066BFF';
const BORDER = '#d0d5dd';
const LABEL = '#212b4e';

const toBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

// nhãn field 14px medium #212b4e (đúng Figma)
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[14px] font-medium" style={{ color: LABEL }}>
    {children}
  </span>
);

export const CreateCampaignModal: React.FC<Props> = ({
  open,
  onClose,
  onCreate,
}) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const goalType: GoalType = Form.useWatch('goalType', form) || 'amount_time';

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      onCreate({
        title: v.title,
        content: v.content,
        imageUrl,
        goalType: v.goalType,
        targetAmount: v.targetAmount,
        startAt: dayjs().toISOString(),
        endAt: v.goalType === 'amount_time' ? v.endAt?.toISOString() : null,
      });
      form.resetFields();
      setImageUrl(undefined);
    } catch {
      /* validation hiển thị sẵn */
    }
  };

  const inputCls = '!rounded-[12px]'; // radius-xl theo Figma

  return (
    <ConfigProvider
      theme={{ token: { fontFamily: "'Be Vietnam Pro', inter, sans-serif" } }}
    >
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      closeIcon={<CloseIcon size={20} className="text-slate-400" />}
      styles={{
        content: { borderRadius: 20, padding: 24 },
        body: { paddingTop: 8 },
      }}
    >
      {/* Tiêu đề căn giữa 30px */}
      <h2 className="text-center text-[30px] font-semibold leading-[38px] tracking-tight mb-5">
        Tạo chiến dịch
      </h2>

      <Form
        form={form}
        layout="vertical"
        initialValues={{ goalType: 'amount_time' }}
        preserve={false}
        requiredMark={false}
        style={{ fontFamily: "'Be Vietnam Pro', inter, sans-serif" }}
      >
        <Form.Item
          name="title"
          label={<FieldLabel>Tiêu đề chiến dịch</FieldLabel>}
          rules={[{ required: true, message: 'Nhập tiêu đề' }]}
        >
          <Input
            size="large"
            className={inputCls}
            placeholder="Nhập tiêu đề"
            maxLength={120}
            suffix={<HelpCircle size={16} className="text-slate-300" />}
          />
        </Form.Item>

        <Form.Item
          name="content"
          label={<FieldLabel>Nội dung chiến dịch</FieldLabel>}
          rules={[{ required: true, message: 'Nhập nội dung chiến dịch' }]}
        >
          <div className="relative">
            <Input.TextArea
              className={inputCls}
              rows={5}
              placeholder="Mô tả mục đích, câu chuyện của chiến dịch..."
              maxLength={2000}
              style={{ resize: 'vertical' }}
            />
            <HelpCircle
              size={16}
              className="text-slate-300 absolute top-3 right-3 pointer-events-none"
            />
          </div>
        </Form.Item>

        {/* Upload box dashed theo Figma */}
        <Form.Item label={<FieldLabel>Hình ảnh / Video</FieldLabel>}>
          <Upload
            accept="image/*,video/*"
            showUploadList={false}
            className="w-full [&_.ant-upload]:!w-full [&_.ant-upload-select]:!w-full"
            beforeUpload={async (file) => {
              if (file.size / 1024 / 1024 > 10) {
                message.error('Mỗi ảnh không quá 10mb');
                return Upload.LIST_IGNORE;
              }
              setImageUrl(await toBase64(file));
              return false; // không upload thật (FE demo)
            }}
          >
            {imageUrl ? (
              <div className="relative">
                <img
                  src={imageUrl}
                  alt="preview"
                  className="w-full max-h-[200px] object-cover rounded-[8px] border border-[#d1cfd2]"
                />
                <span className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
                  Đổi ảnh
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 h-[126px] w-full rounded-[8px] border border-dashed border-[#d1cfd2] cursor-pointer hover:border-[#066BFF] transition-colors">
                <ImagePlus size={24} className="text-[#066BFF]" />
                <span className="text-[14px]" style={{ color: BRAND }}>
                  Upload ảnh/video
                </span>
                <span
                  className="text-[12px] text-center text-[#51545a] leading-snug"
                  style={{ fontFamily: "'Quicksand', sans-serif" }}
                >
                  (Định dạng ảnh .jpg, .png
                  <br />
                  Mỗi ảnh không quá 10mb)
                </span>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="goalType"
          label={<FieldLabel>Chọn hình thức chiến dịch</FieldLabel>}
        >
          <Radio.Group className="flex flex-col gap-2 [&_.ant-radio-inner]:!w-5 [&_.ant-radio-inner]:!h-5 [&_.ant-radio-inner::after]:!scale-100">
            <Radio value="amount_time" className="!text-[16px] !items-center">
              Mục tiêu số tiền + thời gian
            </Radio>
            <Radio value="amount" className="!text-[16px] !items-center">
              Mục tiêu số tiền + không giới hạn thời gian
            </Radio>
          </Radio.Group>
        </Form.Item>

        {/* Số tiền + Thời gian: 2 cột */}
        <div className="flex gap-3">
          <Form.Item
            name="targetAmount"
            className="flex-1"
            label={<FieldLabel>Số tiền mục tiêu</FieldLabel>}
            rules={[
              { required: true, message: 'Nhập số tiền mục tiêu' },
              { type: 'number', min: 10000, message: 'Tối thiểu 10.000 ca' },
            ]}
          >
            <InputNumber<number>
              size="large"
              className={`w-full ${inputCls}`}
              style={{ width: '100%' }}
              min={10000}
              step={100000}
              controls={false}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              parser={(v) => Number((v || '').replace(/\./g, ''))}
              suffix={<Banknote size={16} className="text-slate-400" />}
              placeholder="Nhập số tiền"
            />
          </Form.Item>

          <Form.Item
            name="endAt"
            className="flex-1"
            label={<FieldLabel>Thời gian kết thúc</FieldLabel>}
            rules={
              goalType === 'amount_time'
                ? [{ required: true, message: 'Chọn thời gian kết thúc' }]
                : []
            }
          >
            <DatePicker
              size="large"
              className={`w-full ${inputCls}`}
              style={{ width: '100%' }}
              showTime
              format="DD/MM/YYYY HH:mm"
              disabled={goalType !== 'amount_time'}
              placeholder="Nhập thời gian"
              disabledDate={(d) => d && d < dayjs().startOf('day')}
            />
          </Form.Item>
        </div>

        {/* Nút submit: pill, căn giữa, auto-width */}
        <div className="flex justify-center mt-2">
          <Button
            type="primary"
            size="large"
            onClick={handleOk}
            className="!rounded-full !px-8"
            style={{
              background: BRAND,
              borderColor: BRAND,
              fontFamily: 'inter, sans-serif',
            }}
          >
            Tạo chiến dịch
          </Button>
        </div>
      </Form>
    </Modal>
    </ConfigProvider>
  );
};
